import { useQuery } from '@tanstack/react-query';
import { search } from '@/components/search/search';

interface UseSearchOptions {
  query: string;
  filters?: string[];
  page?: number;
  perPage?: number;
  enabled?: boolean;
  /** When true, filter to is_partner_specific=false (default: true for non-partner users) */
  excludePartnerSpecific?: boolean;
}

/**
 * React Query hook for search API calls with automatic caching and deduplication
 */
export function useSearch({
  query,
  filters = [],
  page = 1,
  perPage = 9,
  enabled = true,
  excludePartnerSpecific = true,
}: UseSearchOptions) {
  // Create stable query key from search parameters (clone before sort to avoid mutating filters)
  const queryKey = ['search', query, [...filters].sort().join(','), page, perPage, excludePartnerSpecific];

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (!query.trim()) {
        return null;
      }
      return await search(query, filters, page, perPage, { excludePartnerSpecific });
    },
    enabled: enabled && !!query.trim(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: false, // Don't refetch if data exists
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 1, // Retry once on failure
  });
}

